import { memo, useEffect, useState } from 'react';

import {
  BadgeCell,
  BtnGroup,
  BtnToolbar,
  Button,
  ButtonAction,
  Col,
  Combobox,
  DateCell,
  Datepicker,
  dateTimeFormatter,
  formatNumber,
  formattedCell,
  type HookFormMethods,
  HookFormResolver,
  Number,
  PageTitle,
  Row,
  Text
} from '@dec/shared';
import { type ColDef, type ValueFormatterParams } from 'ag-grid-community';
import { ActionsGrid } from 'app/components/grid';
import { type ActionColumn } from 'app/components/grid/actions-column';
import GridApplication from 'app/components/grid/gridApplication';
import { useAuthUserContext } from 'app/core/security/authSession';
import { type HttpApiResponse } from 'app/http/types';
import { type SearchTaxReturn, type TaxReturn } from 'app/modules/Reports/TaxReturnList/types';
import { useGetAllFormStatement } from 'app/modules/TaxStatement/FormAdministration/hooks';
import * as Icon from 'react-bootstrap-icons';
import { FileEarmarkText } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { taxreturnTypesCollection } from 'utils/taxreturn-types';
import { yesNoCollection } from 'utils/yes-no';

import { baseUrlTaxreturns } from '@/app/modules/Reports/TaxReturnList/services/taxReturn.service';
import { taxreturnStatesCollection } from '@/utils/taxreturn-states';

import { getTaxReturnForAmendment } from '../../Statements/TaxReturn/services/taxReturn.service';

interface Props {
  amendmentView?: boolean;
}

export const TaxReturnListPage = memo((props: Props) => {
  const [search, setSearch] = useState({ relations: ['FormVersion.Form'] } as SearchTaxReturn);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const { data: forms, loading: loadingForms } = useGetAllFormStatement();

  const auth = useAuthUserContext();
  const gotToDetail = (id: string) => {
    // navigate(`/taxpayer/tax-returns/${id}/general`);

    const url = `/tax-returns/view/${id}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const onSearch = (values: SearchTaxReturn) => {
    values.country = values.country?.code;
    values.active = props.amendmentView ? true : values.active?.id;
    values.taxReturnType = values.taxReturnType?.id;
    values.formId = values.formId?.id;
    values.taxReturnState = values.taxReturnState?.id;
    values.relations = ['FormVersion.Form'];
    setSearch(values);
  };
  useEffect(() => {
    if (props.amendmentView) {
      onSearch({});
    }
  }, []);
  const actions: Array<ActionColumn> = [
    {
      icon: Icon.Search,
      variant: 'secondary',
      onClick: async (row: TaxReturn) => {
        if (!props.amendmentView) {
          gotToDetail(row.id as string);
        } else {
          const api: HttpApiResponse<TaxReturn> = await getTaxReturnForAmendment({
            taxId: row.tax?.id,
            periodicityItem: row.taxPeriodicityItemId ? row.taxPeriodicityItemId : '',
            periodicityType: row.taxPeriodicityTypeId,
            taxpayerId: auth?.userProfile?.taxpayerId,
            year: row.year
          });
          if (!api.error) {
            navigate('/statements/amendment-create', { state: api.response });
          }
        }
      }
    }
  ];

  const colDef: Array<ColDef> = [
    {
      headerName: t('common.label.actions'),
      cellRenderer: ActionsGrid,
      cellRendererParams: {
        actions: actions
      }
    },

    {
      headerName: t('config.tinLegend'),
      field: 'taxReturnNumber'
    },
    {
      headerName: t('common.label.createDate'),
      field: 'createdDate',
      ...formattedCell(dateTimeFormatter, DateCell)
    },
    {
      headerName: t('taxReturns.taxReturnType'),
      field: 'taxReturnType',
      valueFormatter: (params: ValueFormatterParams) => {
        return t(`common.taxreturn-types.${params.value}`);
      }
    },
    {
      headerName: t('taxReturns.taxReturnStatus'),
      field: 'taxReturnStatus',
      ...formattedCell(
        (params: ValueFormatterParams): string => {
          return t(`common.taxreturn-status.${params.value}`);
        },
        BadgeCell((params) => {
          switch (params.value) {
            case 'accepted':
              return 'success';
            case 'pending':
              return 'secondary';
            default:
              return 'danger';
          }
        })
      )
    },
    {
      headerName: t('taxReturns.taxReturnForm'),
      field: 'formVersion.form.code'
    },
    {
      headerName: t('taxReturns.taxReturnActive'),
      field: 'active',
      valueFormatter: (params: ValueFormatterParams) => {
        return t(`common.yes-no.${params.value ? 'yes' : 'no'}`);
      }
    },
    {
      headerName: t('common.label.period'),
      field: 'period'
    },
    {
      headerName: t('common.label.amount'),
      field: 'taxReturnTotal',
      cellStyle: () => {
        return { 'text-align': 'right' };
      },
      valueFormatter: (params: ValueFormatterParams) => {
        return formatNumber(params.value);
      }
    }
  ];

  return (
    <>
      <PageTitle
        title={t('taxpayer-taxreturns.title.page')}
        subtitle={t('taxpayer-taxreturns.title.subtitle')}
        icon={FileEarmarkText}
      />
      <HookFormResolver onSubmit={onSearch} initialValues={{ relations: ['FormVersion.Form'] }}>
        {({ formState: { errors }, reset, setValue, control }: HookFormMethods) => {
          return (
            <>
              <Row>
                <Col>
                  <Combobox
                    data={!loadingForms ? forms : []}
                    code={'id'}
                    display={'shortName'}
                    label={t('taxReturn.taxReturnForm')}
                    name="formId"
                  />
                </Col>
                <Col>
                  <Number label={t('common.label.period')} name="period" />
                </Col>
              </Row>
              <Row>
                <Col>
                  <Text label={t('config.tinLegend')} name="taxreturnNumber" />
                </Col>
                <Col>
                  <Combobox
                    data={taxreturnTypesCollection()}
                    code={'id'}
                    display={'name'}
                    label={t('taxReturn.taxReturnType')}
                    name="taxReturnType"
                  />
                </Col>
              </Row>

              <Row>
                <Col>
                  <Datepicker label={t('common.label.periodFrom')} name="periodFrom" />
                </Col>
                <Col>
                  <Datepicker label={t('common.label.periodFrom')} name="periodTo" />
                </Col>
              </Row>
              {!props.amendmentView && (
                <Row>
                  <Col>
                    <Combobox
                      data={yesNoCollection()}
                      code={'id'}
                      display={'name'}
                      label={t('taxReturn.taxReturnActive')}
                      name="active"
                    />
                  </Col>
                  <Col>
                    <Combobox
                      data={taxreturnStatesCollection()}
                      code={'id'}
                      display={'name'}
                      label={t('taxReturn.taxReturnStatus')}
                      name="taxReturnState"
                    />
                  </Col>
                </Row>
              )}
              <BtnToolbar>
                <BtnGroup>
                  <Button
                    action={ButtonAction.clean}
                    type={'reset'}
                    onClick={() => {
                      onSearch({});
                      //  setSearch({ relations: ['FormVersion.Form'] } as SearchTaxreturn);
                      reset({
                        active: null,
                        formId: null,
                        country: null,
                        taxReturnType: null,
                        period: null,
                        taxReturnState: null,
                        periodFrom: null,
                        periodTo: null,
                        relations: ['FormVersion.Form']
                      });
                    }}
                  />
                </BtnGroup>
                <BtnGroup>
                  <Button action={ButtonAction.find} type="submit" variant="primary" />
                </BtnGroup>
              </BtnToolbar>
            </>
          );
        }}
      </HookFormResolver>
      <Row>
        <Col>
          <GridApplication
            url={`${baseUrlTaxreturns}/from/taxpayer`}
            columnDefs={colDef}
            search={search}
          />
        </Col>
      </Row>
    </>
  );
});

TaxReturnListPage.displayName = 'TaxReturnListPage';
